import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../finance/auth/AuthContext';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import companyLogo from '../assets/logo.png';
import toast from 'react-hot-toast';
import { baseURL } from '../finance/api/client';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${baseURL}/auth/signin`,
        { username: formData.username, password: formData.password }
      );

      if (response.status === 200 && response.data) {
        const { token, type, username, role, id } = response.data;
        const loggedIn = login({ token, tokenType: type, username, role, id });

        toast.success('Successfully logged in!');

        // Redirect all users to Tasks page on login
        navigate('/my-space/tasks');
      } else {
        toast.error('Login failed. Invalid response from server.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100/70 font-sans p-3 sm:p-6 lg:p-8">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100 my-auto">
        {/* Branding Section — Top Banner on Mobile, Left Column on Laptop */}
        <div className="w-full lg:w-1/2 bg-slate-900 flex flex-col items-center justify-center py-8 px-6 sm:py-10 sm:px-8 lg:p-12 text-white relative overflow-hidden shrink-0">
          {/* Decorative glows */}
          <div className="absolute top-[-20%] left-[-10%] w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-blue-600/20 blur-3xl mix-blend-screen pointer-events-none"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-indigo-500/20 blur-3xl mix-blend-screen pointer-events-none"></div>

          <div className="relative z-10 flex flex-col items-center justify-center my-auto">
            <img
              src={companyLogo}
              alt="Orakris Logo"
              className="w-44 sm:w-56 lg:w-64 h-auto object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500 ease-out"
            />
          </div>

          <div className="relative z-10 w-full text-center mt-auto pt-6 hidden lg:block">
            <p className="text-sm text-slate-400/80 font-medium tracking-wide">© 2026 Orakris. All rights reserved.</p>
          </div>
        </div>

        {/* Form Section — Bottom on Mobile, Right Column on Laptop */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center p-6 sm:p-10 lg:p-16 relative bg-white">
          <div className="max-w-md w-full mx-auto">
            <div className="mb-8 sm:mb-10 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1.5 tracking-tight">Welcome Back</h2>
              <p className="text-slate-500 text-sm sm:text-base">Please sign in to your account.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Username</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <FiMail className="text-slate-400 text-base" />
                  </div>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-3 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-colors shadow-2xs bg-white placeholder-slate-400"
                    placeholder="Enter your email or ID"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-semibold text-slate-700">Password</label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs sm:text-sm font-medium text-sky-600 hover:text-sky-500 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <FiLock className="text-slate-400 text-base" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-colors shadow-2xs bg-white placeholder-slate-400"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <FiEyeOff className="text-base" /> : <FiEye className="text-base" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-slate-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2.5 block text-xs sm:text-sm text-slate-600 cursor-pointer select-none">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-8 text-center space-y-3">
              <p className="text-xs sm:text-sm text-slate-500">
                Need help accessing your account?{' '}
                <button type="button" className="font-semibold text-sky-600 hover:text-sky-500 transition-colors">
                  Contact IT Support
                </button>
              </p>
              <p className="text-xs text-slate-400 font-medium lg:hidden">
                © 2026 Orakris. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;